/**
 * Who is signed in, and what they may do.
 *
 * One source of truth: `/api/v1/auth/me`. The console never infers its own
 * permissions — if the server says a role, that is the role, and a page that
 * hides a button still cannot perform the action, because the check that
 * matters is the one on the server.
 *
 * Hiding controls a role cannot use is a courtesy, not a security boundary.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api, ApiError, roleAllows, setUnauthorizedHandler, type CurrentUser, type Role } from './api'

interface AuthState {
  user: CurrentUser | null
  /** True until the first `me` call settles, so the app does not flash a login form. */
  loading: boolean
  error: string | null
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  can: (required: Role) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setUser(await api.get<CurrentUser>('/api/v1/auth/me'))
    } catch {
      // A 401 here is the ordinary "not signed in" case, not a fault.
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Any 401 from anywhere drops us back to signed-out, so an expired or revoked
  // session shows the login form instead of a page full of failed panels.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(null)
  }, [])

  const signIn = useCallback(async (username: string, password: string) => {
    setError(null)
    try {
      const result = await api.post<{ user: CurrentUser }>('/api/v1/auth/login', {
        username,
        password,
      })
      setUser(result.user)
    } catch (err) {
      // Deliberately not more specific: the server does not say whether the
      // username exists, and neither should we.
      setError(err instanceof ApiError ? err.detail : String(err))
      throw err
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout')
    } finally {
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      signIn,
      signOut,
      refresh,
      can: (required: Role) => roleAllows(user?.role, required),
    }),
    [user, loading, error, signIn, signOut, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth used outside AuthProvider')
  return context
}
